import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partiesApi } from '../api/parties';
import type { PartyLedgerResponse } from '../api/parties';
import type { Party, PartyLedgerEntry } from '@coreledger/shared-types';
import Pagination from '../components/Pagination';
import { confirm } from '../components/ConfirmDialog';
import { useRole } from '../context/AuthContext';

const fmt = (n: number) => '₹' + (n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu and Kashmir','Ladakh'];
const emptyParty = (): Partial<Party> => ({ name: '', type: 'customer', country: 'India' });

const CSV_IMPORT_COLUMNS: { key: string; required: boolean; note: React.ReactNode }[] = [
  { key: 'name', required: true, note: "The party's display name." },
  { key: 'type', required: false, note: <><code>customer</code>, <code>vendor</code>, or <code>both</code> — defaults to <code>customer</code> if left blank.</> },
  { key: 'tradeName', required: false, note: <>Trading/brand name, if different from the legal name above — also used to auto-match this party against a bank statement narration (see Banking).</> },
  { key: 'gstin', required: false, note: <>Format <code>22AAAAA0000A1Z5</code>. A row with an invalid GSTIN is skipped, so leave blank if unsure.</> },
  { key: 'pan', required: false, note: <>Format <code>AAAAA0000A</code>. Needed for TDS even when a vendor has no GSTIN.</> },
  { key: 'isRcm', required: false, note: <><code>true</code>/<code>yes</code> marks this party as liable under GST Reverse Charge — leave blank for no.</> },
  { key: 'phone', required: false, note: 'Any format.' },
  { key: 'email', required: false, note: '' },
  { key: 'city', required: false, note: '' },
  { key: 'state', required: false, note: '' },
  { key: 'openingBalance', required: false, note: <>Existing balance as of today, e.g. from a prior system — leave blank or <code>0</code> for a brand-new party.</> },
  { key: 'openingBalanceType', required: false, note: <><code>debit</code> (party owes you) or <code>credit</code> (you owe party) — defaults to <code>debit</code> when a balance is given but the type is left blank.</> },
];
const SAMPLE_PARTIES_CSV = [
  'name,type,tradeName,gstin,pan,isRcm,phone,email,city,state,openingBalance,openingBalanceType',
  'Acme Traders,customer,Acme,27AAAAA0000A1Z5,AAAAA0000A,,9876543210,acme@example.com,Mumbai,Maharashtra,15000,debit',
  'Bharat Suppliers,vendor,,,BBBBB1111B,true,9123456780,contact@bharatsuppliers.in,Pune,Maharashtra,5000,credit',
].join('\n');

function downloadCsv(content: string, filename: string) {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function PartiesPage() {
  const qc = useQueryClient();
  const { canDelete } = useRole();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<null | 'create' | Party>(null);
  const [ledgerParty, setLedgerParty] = useState<Party | null>(null);
  const [form, setForm] = useState<Partial<Party>>(emptyParty());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [csvInfoModal, setCsvInfoModal] = useState(false);
  const [importResult, setImportResult] = useState<{ ok: number; dup: number; fail: number; dupNames: string[] } | null>(null);
  const csvRef = useRef<HTMLInputElement>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: partiesData, isLoading } = useQuery({
    queryKey: ['parties', debouncedSearch, typeFilter, page],
    queryFn: () => partiesApi.list(debouncedSearch || undefined, page, 50, typeFilter || undefined),
    staleTime: 30 * 1000,
  });
  const parties = partiesData?.data ?? [];
  const partiesMeta = partiesData?.meta;
  const { data: ledgerData, isLoading: loadingLedger } = useQuery({
    queryKey: ['party-ledger', ledgerParty?.id],
    queryFn: () => partiesApi.ledger(ledgerParty!.id),
    enabled: !!ledgerParty,
    staleTime: 30 * 1000,
  });
  const ledger: PartyLedgerEntry[] = ledgerData?.entries ?? [];
  const ledgerSummary: PartyLedgerResponse['summary'] | undefined = ledgerData?.summary;
  const del = useMutation({ mutationFn: partiesApi.delete, onSuccess: () => { qc.invalidateQueries({ queryKey: ['parties'] }); qc.refetchQueries({ type: 'all' }); } });

  // Bulk-select — same Set<string>-of-ids pattern already used on
  // InvoicesPage.tsx/LorryReceiptsPage.tsx/BankTransactionsPage.tsx.
  // Selection only ever spans the current (server-paginated/filtered) page's
  // own rows; deleteAll (below) is the separate, unfiltered "everything"
  // action.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const bulkDel = useMutation({
    mutationFn: partiesApi.bulkDelete,
    onSuccess: () => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ['parties'] }); qc.refetchQueries({ type: 'all' }); },
  });
  const delAll = useMutation({
    mutationFn: partiesApi.deleteAll,
    onSuccess: () => { setSelected(new Set()); setPage(1); qc.invalidateQueries({ queryKey: ['parties'] }); qc.refetchQueries({ type: 'all' }); },
  });

  useEffect(() => {
    if (searchParams.get('new') === '1') { setForm(emptyParty()); setError(null); setModal('create'); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function openCreate() { setForm(emptyParty()); setError(null); setModal('create'); }
  function openEdit(p: Party) { setForm({ ...p }); setError(null); setModal(p); }

  function set(k: keyof Party) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm(f => ({ ...f, [k]: e.target.value }));
  }

  async function lookupPin(pin: string) {
    if (pin.length !== 6) return;
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json() as [{ Status: string; PostOffice?: { Name: string; District: string; State: string }[] }];
      if (data[0]?.Status === 'Success' && data[0].PostOffice?.length) {
        const po = data[0].PostOffice[0];
        setForm(f => ({ ...f, city: f.city || po.District, state: f.state || po.State }));
      }
    } catch { /* silently fail */ }
  }

  const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i;

  async function handleSave() {
    if (!form.name?.trim()) { setError('Name is required'); return; }
    if (form.gstin?.trim() && !GSTIN_RE.test(form.gstin.trim())) {
      setError('Invalid GSTIN. Format: 22AAAAA0000A1Z5 (15 characters)');
      return;
    }
    setError(null); setSaving(true);
    try {
      if (modal === 'create') { await partiesApi.create(form); }
      else { await partiesApi.update((modal as Party).id, form); }
      qc.invalidateQueries({ queryKey: ['parties'] });
      qc.refetchQueries({ type: 'all' });
      setModal(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  // tradeName/pan are identity fields (like gstin) — included in the
  // duplicate signature, since two rows with the same name but different
  // PAN/trade name are more likely two different real parties than a true
  // duplicate. openingBalance/openingBalanceType/isRcm are deliberately
  // excluded — they're attributes, not identity: re-importing the same
  // party with a corrected balance shouldn't stop matching as a duplicate
  // and create a second row.
  const SIG_FIELDS = ['name', 'type', 'tradeName', 'gstin', 'pan', 'phone', 'email', 'city', 'state'] as const;
  type PartySig = Record<typeof SIG_FIELDS[number], string>;
  const normalizeSig = (v?: string | null) => (v ?? '').trim().toLowerCase();
  // A CSV row counts as a duplicate of an existing party only when every
  // field the row *actually provides* matches — a field left blank in the
  // row is never compared, so a minimal row (just a name) matches on name
  // alone, while a fuller row only flags a true duplicate instead of two
  // different people who happen to share a name.
  function isDuplicateSig(row: Partial<PartySig>, existing: PartySig): boolean {
    return SIG_FIELDS.every(f => !row[f] || row[f] === existing[f]);
  }

  // Same columns as SAMPLE_PARTIES_CSV so an exported file can be re-imported elsewhere unchanged.
  async function handleExportCsv() {
    setExporting(true);
    try {
      const rows: string[] = ['name,type,tradeName,gstin,pan,isRcm,phone,email,city,state,openingBalance,openingBalanceType'];
      let page = 1;
      for (;;) {
        const res = await partiesApi.list(undefined, page, 200);
        for (const p of res.data) {
          rows.push([
            p.name, p.type, p.tradeName, p.gstin, p.pan, p.isRcm ? 'true' : '', p.phone, p.email, p.city, p.state,
            p.openingBalance ?? '', p.openingBalanceType ?? '',
          ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        }
        if (page >= (res.meta?.totalPages ?? 1)) break;
        page++;
      }
      downloadCsv(rows.join('\n'), 'parties.csv');
    } finally {
      setExporting(false);
    }
  }

  async function fetchAllPartySigs(): Promise<PartySig[]> {
    const sigs: PartySig[] = [];
    let page = 1;
    for (;;) {
      const res = await partiesApi.list(undefined, page, 200);
      for (const p of res.data) {
        sigs.push({
          name: normalizeSig(p.name), type: normalizeSig(p.type), tradeName: normalizeSig(p.tradeName), gstin: normalizeSig(p.gstin), pan: normalizeSig(p.pan),
          phone: normalizeSig(p.phone), email: normalizeSig(p.email), city: normalizeSig(p.city), state: normalizeSig(p.state),
        });
      }
      if (page >= (res.meta?.totalPages ?? 1)) break;
      page++;
    }
    return sigs;
  }

  async function handleCsvImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) { alert('CSV file is empty or missing data rows.'); return; }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z]/g, ''));
    const get = (row: string[], key: string) => { const i = headers.indexOf(key); return i >= 0 ? (row[i] ?? '').trim().replace(/^"|"$/g, '') : ''; };
    setImporting(true);
    const knownSigs = await fetchAllPartySigs();
    let ok = 0, fail = 0, dup = 0;
    const dupNames: string[] = [];
    for (const line of lines.slice(1)) {
      const cols = line.split(',');
      const name = get(cols, 'name');
      if (!name) { fail++; continue; }
      const rawType = get(cols, 'type');
      const type = (rawType as Party['type']) || 'customer';
      const tradeName = get(cols, 'tradename'), gstin = get(cols, 'gstin'), pan = get(cols, 'pan');
      const phone = get(cols, 'phone'), email = get(cols, 'email'), city = get(cols, 'city'), state = get(cols, 'state');
      const isRcm = /^(true|yes|1|y)$/i.test(get(cols, 'isrcm'));
      const openingBalanceRaw = get(cols, 'openingbalance');
      const openingBalance = openingBalanceRaw ? parseFloat(openingBalanceRaw) : undefined;
      const openingBalanceTypeRaw = get(cols, 'openingbalancetype').toLowerCase();
      const openingBalanceType = openingBalance ? (openingBalanceTypeRaw === 'credit' ? 'credit' : 'debit') : undefined;
      // Matched against rawType (blank when the row doesn't specify one),
      // not the defaulted type — otherwise every type-less row would
      // default to "customer" and only ever match existing customers.
      const rowSig: Partial<PartySig> = { name: normalizeSig(name), type: normalizeSig(rawType), tradeName: normalizeSig(tradeName), gstin: normalizeSig(gstin), pan: normalizeSig(pan), phone: normalizeSig(phone), email: normalizeSig(email), city: normalizeSig(city), state: normalizeSig(state) };
      if (knownSigs.some(sig => isDuplicateSig(rowSig, sig))) { dup++; dupNames.push(name); continue; }
      try {
        await partiesApi.create({
          name, type,
          tradeName: tradeName || undefined, gstin: gstin || undefined, pan: pan || undefined,
          isRcm: isRcm || undefined, phone: phone || undefined,
          email: email || undefined, city: city || undefined, state: state || undefined,
          openingBalance, openingBalanceType,
        });
        ok++;
        // So a second occurrence of the same row later in the same file
        // is also caught, not just duplicates of already-existing parties.
        knownSigs.push({ name: normalizeSig(name), type: normalizeSig(type), tradeName: normalizeSig(tradeName), gstin: normalizeSig(gstin), pan: normalizeSig(pan), phone: normalizeSig(phone), email: normalizeSig(email), city: normalizeSig(city), state: normalizeSig(state) });
      } catch { fail++; }
    }
    setImporting(false);
    qc.invalidateQueries({ queryKey: ['parties'] });
    qc.refetchQueries({ type: 'all' });
    setImportResult({ ok, dup, fail, dupNames });
    if (e.target) e.target.value = '';
  }

  const closingBalance = ledgerSummary ? ledgerSummary.totalDebit - ledgerSummary.totalCredit : 0;

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Parties</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-ghost" onClick={handleExportCsv} disabled={exporting}>
            {exporting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Exporting…</> : '⬇ Export CSV'}
          </button>
          <button className="btn btn-ghost" onClick={() => setCsvInfoModal(true)} disabled={importing}>
            {importing ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Importing…</> : '📥 Import CSV'}
          </button>
          <button className="btn btn-primary" onClick={openCreate}>+ Add Party</button>
          {canDelete && partiesMeta && partiesMeta.total > 0 && (
            <>
              {/* Visual separator — "Delete All" wipes the whole list, not a
                  peer action of the routine buttons to its left, so it gets
                  its own outline style + a divider instead of lining up as a
                  fifth equal-weight button. */}
              <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 0.25rem' }} />
              <button
                className="btn btn-danger-outline"
                disabled={delAll.isPending}
                onClick={async () => {
                  const count = partiesMeta.total;
                  if (!(await confirm({
                    title: 'Delete ALL parties?',
                    message: `This removes all ${count} part${count === 1 ? 'y' : 'ies'} in this account from every list, report, and dropdown. This cannot be undone from the UI.`,
                    confirmLabel: `Delete all ${count}`,
                    danger: true,
                  }))) return;
                  // A second, explicit confirm for an action this destructive —
                  // one misclick shouldn't be able to wipe an entire party list;
                  // ConfirmDialog has no type-to-confirm input, so this is the
                  // cheapest meaningful extra friction available.
                  if (!(await confirm({
                    title: 'Are you absolutely sure?',
                    message: `Confirming again will permanently remove all ${count} parties right now.`,
                    confirmLabel: 'Yes, delete everything',
                    danger: true,
                  }))) return;
                  delAll.mutate();
                }}
              >
                {delAll.isPending ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '🗑 Delete All'}
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, GSTIN, phone, email…" style={{ maxWidth: 320 }} />
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} style={{ width: 'auto' }}>
          <option value="">All types</option>
          <option value="customer">Customers</option>
          <option value="vendor">Vendors</option>
          <option value="both">Both</option>
        </select>
      </div>

      {selected.size > 0 && (
        <div className="card" style={{ padding: '0.85rem 1.25rem', marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: '0.85rem' }}>{selected.size} selected</strong>
          <button
            className="btn btn-danger btn-sm"
            disabled={bulkDel.isPending}
            onClick={async () => {
              const count = selected.size;
              if (!(await confirm({ message: `Delete ${count} selected part${count === 1 ? 'y' : 'ies'}? This cannot be undone from the UI.`, danger: true, confirmLabel: `Delete ${count}` }))) return;
              bulkDel.mutate(Array.from(selected));
            }}
          >
            {bulkDel.isPending ? <span className="spinner" style={{ width: 12, height: 12 }} /> : `Delete Selected (${selected.size})`}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>Clear selection</button>
        </div>
      )}

      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : parties.length === 0 ? <div className="empty-state"><div className="empty-state-icon">🏢</div><div className="empty-state-text">No parties found</div></div>
          : (
            <table>
              <thead><tr>
                {canDelete && (
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox" style={{ width: 'auto', cursor: 'pointer' }}
                      checked={parties.length > 0 && parties.every(p => selected.has(p.id))}
                      onChange={e => setSelected(e.target.checked ? new Set(parties.map(p => p.id)) : new Set())}
                    />
                  </th>
                )}
                <th>Name</th><th>Type</th><th>GSTIN</th><th>Phone</th><th>City</th><th style={{ textAlign: 'right' }}>Balance</th><th style={{ width: 140 }}>Actions</th></tr></thead>
              <tbody>
                {parties.map(p => (
                  <tr key={p.id} style={{ background: selected.has(p.id) ? 'var(--primary-light)' : undefined }}>
                    {canDelete && (
                      <td>
                        <input
                          type="checkbox" style={{ width: 'auto', cursor: 'pointer' }}
                          checked={selected.has(p.id)}
                          onChange={e => setSelected(prev => { const s = new Set(prev); e.target.checked ? s.add(p.id) : s.delete(p.id); return s; })}
                        />
                      </td>
                    )}
                    <td>
                      <span style={{ fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline dotted' }} onClick={() => setLedgerParty(p)}>{p.name}</span>
                      {p.tradeName && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{p.tradeName}</div>}
                    </td>
                    <td><span className="badge badge-gray" style={{ textTransform: 'capitalize' }}>{p.type}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{p.gstin ?? '—'}</td>
                    <td>{p.phone ?? '—'}</td>
                    <td>{p.city ?? '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>—</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setLedgerParty(p)}>Ledger</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}>Edit</button>
                        {canDelete && <button className="btn btn-danger btn-sm" disabled={del.isPending} onClick={async () => { if (await confirm({ message: `Delete ${p.name}?`, danger: true })) del.mutate(p.id); }}>Del</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {partiesMeta && partiesMeta.totalPages > 1 && (
          <Pagination page={page} totalPages={partiesMeta.totalPages} onChange={setPage} />
        )}
      </div>

      {/* ── Party Ledger Drawer ────────────────────────────────────────────── */}
      {ledgerParty && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 680, background: 'var(--surface)', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', zIndex: 200, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{ledgerParty.name}</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                {ledgerParty.gstin ? `GSTIN: ${ledgerParty.gstin}` : ledgerParty.city ?? ''}
                {' · '}
                <span className="badge badge-gray" style={{ textTransform: 'capitalize', fontSize: '0.72rem' }}>{ledgerParty.type}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Closing Balance</div>
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: closingBalance > 0 ? 'var(--danger)' : closingBalance < 0 ? 'var(--success)' : 'var(--text)' }}>
                  {fmt(Math.abs(closingBalance))} {closingBalance > 0 ? 'Dr' : closingBalance < 0 ? 'Cr' : ''}
                </div>
              </div>
              <button onClick={() => setLedgerParty(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '0 0.25rem' }}>×</button>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingLedger ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><span className="spinner" /></div>
            ) : ledger.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No transactions yet for this party</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
              <table>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th style={{ textAlign: 'right' }}>Debit (Dr)</th>
                    <th style={{ textAlign: 'right' }}>Credit (Cr)</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((entry: PartyLedgerEntry, i) => (
                    <tr key={i} style={{ background: entry.type === 'opening' ? 'var(--surface-alt)' : undefined }}>
                      <td style={{ fontSize: '0.85rem' }}>{entry.date}</td>
                      <td>
                        <span className={`badge ${entry.type === 'payment' ? 'badge-paid' : entry.type === 'invoice' ? 'badge-blue' : entry.type === 'bank' ? 'badge-orange' : 'badge-gray'}`}
                          style={{ fontSize: '0.7rem', textTransform: 'capitalize' }}>{entry.type}</span>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>
                        {entry.description}
                        {entry.invoiceNumber && <span style={{ marginLeft: '0.4rem', color: 'var(--text-muted)', fontSize: '0.78rem' }}>#{entry.invoiceNumber}</span>}
                      </td>
                      <td style={{ textAlign: 'right', color: entry.debit > 0 ? 'var(--danger)' : 'var(--text-muted)', fontWeight: entry.debit > 0 ? 600 : 400 }}>
                        {entry.debit > 0 ? fmt(entry.debit) : '—'}
                      </td>
                      <td style={{ textAlign: 'right', color: entry.credit > 0 ? 'var(--success)' : 'var(--text-muted)', fontWeight: entry.credit > 0 ? 600 : 400 }}>
                        {entry.credit > 0 ? fmt(entry.credit) : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: entry.balance > 0 ? 'var(--danger)' : entry.balance < 0 ? 'var(--success)' : 'var(--text)' }}>
                        {fmt(Math.abs(entry.balance))} {entry.balance > 0 ? 'Dr' : entry.balance < 0 ? 'Cr' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>

          <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => openEdit(ledgerParty)}>Edit Party</button>
          </div>
        </div>
      )}
      {ledgerParty && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 199 }} onClick={() => setLedgerParty(null)} />}

      {/* ── Import CSV — column info + sample file, shown before the file picker ── */}
      <input ref={csvRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvImport} />
      {csvInfoModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCsvInfoModal(false)}>
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <span className="modal-title">Import Parties from CSV</span>
              <button className="modal-close" onClick={() => setCsvInfoModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.85rem', color: 'var(--text-2)', margin: '0 0 1.1rem' }}>
                Your file needs a header row. Only <strong>name</strong> is required — everything else is optional and can be filled in later.
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginBottom: '1.1rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '0 0.6rem 0.5rem', borderBottom: '1px solid var(--border)' }}>Column</th>
                    <th style={{ textAlign: 'left', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '0 0.6rem 0.5rem', borderBottom: '1px solid var(--border)' }}>Required?</th>
                    <th style={{ textAlign: 'left', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '0 0.6rem 0.5rem', borderBottom: '1px solid var(--border)' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {CSV_IMPORT_COLUMNS.map(c => (
                    <tr key={c.key}>
                      <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--border)', fontWeight: 700, fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', fontSize: '0.8rem' }}>{c.key}</td>
                      <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--border)' }}>
                        {c.required
                          ? <span style={{ display: 'inline-flex', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, background: 'var(--warning-light, #fffbeb)', color: 'var(--warning)' }}>Required</span>
                          : <span style={{ display: 'inline-flex', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, background: 'var(--surface-alt)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Optional</span>}
                      </td>
                      <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '0.79rem' }}>{c.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', background: 'var(--primary-light)', border: '1px dashed var(--primary)', borderRadius: 8, padding: '0.75rem 1rem' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>Not sure where to start?</div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>Download a filled-in example with these exact columns.</div>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ flexShrink: 0, borderColor: 'var(--primary)', color: 'var(--primary)' }}
                  onClick={() => downloadCsv(SAMPLE_PARTIES_CSV, 'sample-parties.csv')}
                >⬇ Sample CSV</button>
              </div>
            </div>
            <div className="modal-footer">
              <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>Rows that exactly match an existing party are skipped, not duplicated.</span>
              <div style={{ display: 'flex', gap: '0.6rem', flexShrink: 0 }}>
                <button className="btn btn-ghost" onClick={() => setCsvInfoModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={() => { setCsvInfoModal(false); csvRef.current?.click(); }}>Choose File</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Import results ─────────────────────────────────────────────────── */}
      {importResult && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setImportResult(null)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <span className="modal-title">Import Complete</span>
              <button className="modal-close" onClick={() => setImportResult(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: importResult.dupNames.length > 0 ? '1rem' : 0 }}>
                <div style={{ flex: 1, textAlign: 'center', background: 'var(--success-light, #f0fdf4)', borderRadius: 8, padding: '0.75rem' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--success)' }}>{importResult.ok}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>Imported</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-2)' }}>{importResult.dup}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>Duplicate, skipped</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', background: importResult.fail > 0 ? 'var(--warning-light, #fffbeb)' : 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: importResult.fail > 0 ? 'var(--warning)' : 'var(--text-2)' }}>{importResult.fail}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>Failed</div>
                </div>
              </div>
              {importResult.dupNames.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>Skipped as duplicates of an existing party:</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', maxHeight: 140, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6, padding: '0.5rem 0.75rem' }}>
                    {importResult.dupNames.join(', ')}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={() => setImportResult(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit Modal ────────────────────────────────────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'Add Party' : 'Edit Party'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-box">{error}</div>}
              <div className="form-row form-row-3">
                <label>Name *<input value={form.name ?? ''} onChange={set('name')} placeholder="Party name" autoFocus /></label>
                <label>Trade Name<input value={form.tradeName ?? ''} onChange={set('tradeName')} placeholder="If different from name" /></label>
                <label>Type
                  <select value={form.type ?? 'customer'} onChange={set('type')}>
                    <option value="customer">Customer</option>
                    <option value="vendor">Vendor</option>
                    <option value="both">Both</option>
                  </select>
                </label>
              </div>
              <div className="form-row form-row-3">
                <label>GSTIN<input value={form.gstin ?? ''} onChange={e => { const v = e.target.value.toUpperCase(); setForm(f => ({ ...f, gstin: v, ...(v.length === 15 && !f.pan ? { pan: v.slice(2, 12) } : {}) })); }} placeholder="22AAAAA0000A1Z5" maxLength={15} style={{ textTransform: 'uppercase' }} /></label>
                <label>PAN<input value={form.pan ?? ''} onChange={set('pan')} placeholder="AAAAA0000A" maxLength={10} /></label>
                <label>Company Type
                  <select value={form.companyType ?? ''} onChange={set('companyType')}>
                    <option value="">— Select —</option>
                    <option value="individual">Individual / Sole Proprietor</option>
                    <option value="partnership">Partnership Firm</option>
                    <option value="llp">LLP</option>
                    <option value="private_limited">Private Limited</option>
                    <option value="public_limited">Public Limited</option>
                    <option value="opc">One Person Company (OPC)</option>
                    <option value="trust">Trust / Society / NGO</option>
                    <option value="government">Government / PSU</option>
                  </select>
                </label>
              </div>
              {(form.companyType === 'private_limited' || form.companyType === 'public_limited' || form.companyType === 'llp' || form.companyType === 'opc') && (
                <div className="form-row" style={{ maxWidth: 360 }}>
                  <label>CIN<input value={form.cin ?? ''} onChange={set('cin')} placeholder="U12345MH2020PTC123456" maxLength={21} /></label>
                </div>
              )}
              <div className="form-row form-row-2">
                <label>Email<input type="email" value={form.email ?? ''} onChange={set('email')} placeholder="contact@company.com" /></label>
                <label>Phone<input value={form.phone ?? ''} onChange={set('phone')} placeholder="+91 9876543210" /></label>
              </div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', margin: '0.75rem 0 0.4rem' }}>Address</div>
              <div className="form-row"><label>Address Line 1<input value={form.addressLine1 ?? ''} onChange={set('addressLine1')} placeholder="Street address" /></label></div>
              <div className="form-row form-row-4">
                <label>City<input value={form.city ?? ''} onChange={set('city')} placeholder="Mumbai" /></label>
                <label>State
                  <select value={form.state ?? ''} onChange={set('state')}>
                    <option value="">— Select —</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label>PIN
                  <input value={form.postalCode ?? ''} onChange={e => { set('postalCode')(e); lookupPin(e.target.value); }} placeholder="400001" maxLength={6} />
                </label>
                <label>Country<input value={form.country ?? 'India'} onChange={set('country')} /></label>
              </div>
              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 'normal', cursor: 'pointer', fontSize: '0.875rem' }}>
                  <input type="checkbox" checked={form.isRcm ?? false} onChange={e => setForm(f => ({ ...f, isRcm: e.target.checked }))} style={{ width: 'auto' }} />
                  Reverse Charge (RCM) applicable for this party
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.75rem 0 0.4rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Billing Address <span style={{ fontWeight: 400, textTransform: 'none' }}>(if different from above)</span></span>
                <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem', padding: '0.15rem 0.55rem' }} onClick={() => setForm(f => ({ ...f, billingAddressLine1: f.addressLine1, billingCity: f.city, billingState: f.state, billingPostalCode: f.postalCode }))}>Copy from Registered</button>
              </div>
              <div className="form-row"><label>Billing Address Line 1<input value={form.billingAddressLine1 ?? ''} onChange={set('billingAddressLine1')} placeholder="Street address" /></label></div>
              <div className="form-row form-row-3">
                <label>Billing City<input value={form.billingCity ?? ''} onChange={set('billingCity')} placeholder="Mumbai" /></label>
                <label>Billing State
                  <select value={form.billingState ?? ''} onChange={set('billingState')}>
                    <option value="">— Select —</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label>Billing PIN<input value={form.billingPostalCode ?? ''} onChange={set('billingPostalCode')} placeholder="400001" maxLength={6} /></label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.75rem 0 0.4rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Shipping Address <span style={{ fontWeight: 400, textTransform: 'none' }}>(delivery location)</span></span>
                <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem', padding: '0.15rem 0.55rem' }} onClick={() => setForm(f => ({ ...f, shippingAddressLine1: f.billingAddressLine1 || f.addressLine1, shippingCity: f.billingCity || f.city, shippingState: f.billingState || f.state, shippingPostalCode: f.billingPostalCode || f.postalCode }))}>Same as Billing</button>
              </div>
              <div className="form-row"><label>Shipping Address Line 1<input value={form.shippingAddressLine1 ?? ''} onChange={set('shippingAddressLine1')} placeholder="Street address" /></label></div>
              <div className="form-row form-row-3">
                <label>Shipping City<input value={form.shippingCity ?? ''} onChange={set('shippingCity')} placeholder="Mumbai" /></label>
                <label>Shipping State
                  <select value={form.shippingState ?? ''} onChange={set('shippingState')}>
                    <option value="">— Select —</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label>Shipping PIN<input value={form.shippingPostalCode ?? ''} onChange={set('shippingPostalCode')} placeholder="400001" maxLength={6} /></label>
              </div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', margin: '0.75rem 0 0.4rem' }}>Opening Balance</div>
              <div className="form-row form-row-2">
                <label>Opening Balance (₹)
                  <input type="number" min={0} step={0.01} value={form.openingBalance ?? ''} onChange={e => setForm(f => ({ ...f, openingBalance: parseFloat(e.target.value) || 0 }))} placeholder="0.00" />
                </label>
                <label>Balance Type
                  <select value={form.openingBalanceType ?? 'debit'} onChange={e => setForm(f => ({ ...f, openingBalanceType: e.target.value as 'debit' | 'credit' }))}>
                    <option value="debit">Debit (party owes you)</option>
                    <option value="credit">Credit (you owe party)</option>
                  </select>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save Party'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
